import { LocalizationString } from '@celito.clients/assets';
import { raiseErrorToast } from '@celito.clients/utils';
import { OptionProps } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { HookFormProps } from '../../../types';
import ControlledDropdown from '../controlled-dropdown/controlled-dropdown.component';
import { getAllObjectsData } from './services';

type ObjectDefinitionsDropdownProps<T extends FieldValues = FieldValues> =
  HookFormProps<T>;

export const ObjectDefinitionsDropdown = <T extends FieldValues>({
  attribute,
  setValue,
  ...props
}: ObjectDefinitionsDropdownProps<T>): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<OptionProps[]>([]);

  // Get parent object name from URL params for dynamic relationship population
  const parentObjectName = searchParams.get('po') ?? undefined;

  // Check if this field should be auto-populated with parent object
  // Check both key and columnName for compatibility
  const isObjectIdField =
    attribute?.key === 'object' || attribute?.columnName === 'objectId';
  const hasParentObject = !!(parentObjectName && isObjectIdField);

  useEffect(() => {
    getAllObjectsData(attribute?.modulesToRestrict)
      .then((r) => {
        const objectOptions = r.map((o) => ({
          value: o.name,
          text: o.label,
        }));
        setOptions(objectOptions);

        // Auto-populate with parent object if this is objectId field
        if (hasParentObject && setValue) {
          // Verify the parent object exists in options
          const matchingOption = objectOptions.find(
            (opt) => opt.value === parentObjectName
          );

          if (matchingOption) {
            // Use longer timeout to ensure dropdown has rendered with options
            setTimeout(() => {
              setValue(
                attribute?.name as Path<T>,
                matchingOption.value as any,
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
              if (attribute?.name === 'object_id__s') {
                setValue(
                  'object_name__s' as Path<T>,
                  matchingOption.value as any
                );
              }
            }, 500);
          }
        }
      })
      .catch((_error) => {
        raiseErrorToast(_error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <ControlledDropdown
      {...{
        ...props,
        attribute: {
          ...attribute,
          placeholder: loading
            ? `${LocalizationString.LOADING}...`
            : attribute.placeholder,
          // Store only the value string, not the whole object
          onChange: (option: any) => option?.value || option,
          valueParser: (value: any) => value,
        },
        options: options,
      }}
    />
  );
};
